import { Link } from '@tanstack/react-router';
import { MailCheck } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { ServerError } from '@/shared/ui/server-error';

import { useRequestMagicLink } from '../hooks/use-request-magic-link';
import { magicLinkRequestSchema, type MagicLinkRequestValues } from '../schemas/two-factor-schemas';

import { AuthCard } from './auth-frame';

/**
 * Email → POST /platform/v1/auth/magic-link. The backend answers 202 whether or not the address
 * has an account, so the confirmation is neutral; 429 (five a minute per IP) is the visible failure.
 */
export function MagicLinkRequestCard() {
  const { t } = useTranslation('auth');
  const request = useRequestMagicLink();
  const form = useForm<MagicLinkRequestValues>({
    resolver: useI18nResolver(magicLinkRequestSchema),
    defaultValues: { email: '' },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await request.mutateAsync(values);
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  if (request.isSuccess) {
    return (
      <AuthCard id="magic-link" title={t('magicLink.sentTitle')}>
        <div role="status" className="flex flex-col gap-4 text-sm">
          <MailCheck className="text-success size-8" aria-hidden />
          <p className="text-muted-foreground leading-relaxed">{t('magicLink.sent')}</p>
          <p className="text-muted-foreground text-xs leading-relaxed">{t('magicLink.sentHint')}</p>
          <Button asChild variant="outline" className="w-full">
            <Link to="/login">{t('frame.backToSignIn')}</Link>
          </Button>
        </div>
      </AuthCard>
    );
  }

  const rateLimited = isApiError(request.error) && request.error.kind === 'rate_limited';

  return (
    <AuthCard id="magic-link" title={t('magicLink.title')} description={t('magicLink.description')}>
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          {request.isError && !request.error.hasFieldErrors ? (
            <ServerError
              error={request.error}
              message={rateLimited ? t('magicLink.rateLimited') : null}
            />
          ) : null}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.email')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder={t('fields.emailPlaceholder')}
                    className="h-11"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full font-semibold"
            disabled={request.isPending}
          >
            {request.isPending ? t('magicLink.submitting') : t('magicLink.submit')}
          </Button>
        </form>
      </Form>
    </AuthCard>
  );
}
